import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleDashed,
  Download,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { supportedFailoverCodes, undetectableSipCodes } from '@klinkpbx/shared';
import { DataTable, type Column } from '../components/data-table';
import {
  FormFooter,
  Grid,
  PageHeader,
  Row,
  Section,
  Select,
  Tabs,
  Toggle,
} from '../components/form';
import { Alert, Badge, Button, cn, Input, Tooltip } from '../components/ui';
import {
  ApiError,
  api,
  type Extension,
  type Trunk,
  type TrunkDetail,
  type TrunkDod,
  type TrunkMode,
} from '../lib/api';

/**
 * The three trunk types, in the manual's language.
 *
 * "Register" and "Account" are the same protocol in opposite directions, and which way round it
 * goes is the single thing admins get wrong when setting one up — so the description says it
 * rather than leaving it to be inferred from a type name.
 */
const TRUNK_TYPES: ReadonlyArray<{ value: TrunkMode; label: string; description: string }> = [
  {
    value: 'register',
    label: 'Register trunk',
    description: 'This PBX registers to the provider with a username and password.',
  },
  {
    value: 'ip_auth',
    label: 'Peer trunk',
    description: 'No registration. The provider is recognised by the address it calls from.',
  },
  {
    value: 'account',
    label: 'Account trunk',
    description: 'The other way round: a device or another PBX registers into this one.',
  },
];

const CODECS = [
  { value: 'ulaw', label: 'u-law (G.711)' },
  { value: 'alaw', label: 'a-law (G.711)' },
  { value: 'g722', label: 'G.722 (HD)' },
  { value: 'g729', label: 'G.729' },
  { value: 'opus', label: 'Opus' },
] as const;

const typeLabel = (mode: TrunkMode) => TRUNK_TYPES.find((t) => t.value === mode)?.label ?? mode;

/**
 * Download the list as CSV.
 *
 * Every field is quoted and embedded quotes doubled: a trunk name can legitimately contain a
 * comma, and one unquoted field shifts every column after it for that row alone — the kind of
 * corruption somebody notices three imports later.
 */
function exportTrunksCsv(rows: ReadonlyArray<Record<string, string>>): void {
  const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const fields = Object.keys(rows[0] ?? { name: '' });
  const csv = [
    fields.map(escape).join(','),
    ...rows.map((row) => fields.map((field) => escape(row[field])).join(',')),
  ].join('\r\n');

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'trunks.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export function Trunks() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);

  const { data: trunks = [], isLoading } = useQuery({
    queryKey: ['trunks'],
    queryFn: () => api.get<Trunk[]>('/api/v1/trunks'),
    // Registration state is live and can lapse at any moment; a status column that only updates
    // on reload is worse than none, because it looks current.
    refetchInterval: 15_000,
  });

  const remove = useMutation({
    mutationFn: async (rows: readonly Trunk[]) => {
      for (const row of rows) await api.del(`/api/v1/trunks/${row.id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trunks'] }),
  });

  if (editing !== null) {
    return (
      <TrunkEditor
        id={editing === 'new' ? null : editing}
        onClose={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['trunks'] });
        }}
      />
    );
  }

  const columns: ReadonlyArray<Column<Trunk>> = [
    {
      key: 'status',
      header: 'Status',
      width: '5rem',
      render: (t) => <TrunkStatus trunk={t} />,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (t) => <span className="font-medium">{t.name}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      sort: (a, b) => a.mode.localeCompare(b.mode),
      render: (t) => typeLabel(t.mode),
    },
    {
      key: 'host',
      header: 'Hostname / Port',
      sort: (a, b) => a.serverHost.localeCompare(b.serverHost),
      // An account trunk has no provider address — the peer comes to us — so showing a blank
      // host reads as a misconfiguration when it is the normal state.
      render: (t) =>
        t.mode === 'account' ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="font-mono text-xs">
            {t.serverHost}:{t.serverPort}
          </span>
        ),
    },
    {
      key: 'username',
      header: 'Username',
      sort: (a, b) => (a.authUsername ?? '').localeCompare(b.authUsername ?? ''),
      render: (t) => <span className="font-mono text-xs">{t.authUsername ?? '—'}</span>,
    },
    {
      key: 'cid',
      header: 'Outbound caller ID',
      render: (t) => t.outboundCallerId ?? <span className="text-muted-foreground">—</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trunk"
        description="Connections to your telephone provider, for calls in and out."
      />

      <DataTable
        rows={trunks}
        columns={columns}
        rowKey={(t) => t.id}
        search={(t) => `${t.name} ${t.serverHost} ${t.authUsername ?? ''}`}
        searchPlaceholder="Search trunks"
        toolbar={
          <span className="flex gap-2">
            <Button onClick={() => setEditing('new')}>
              <Plus className="mr-1.5 size-4" />
              Add
            </Button>
            <Button
              variant="secondary"
              disabled={trunks.length === 0}
              onClick={() =>
                exportTrunksCsv(
                  trunks.map((t) => ({
                    name: t.name,
                    type: typeLabel(t.mode),
                    host: t.mode === 'account' ? '' : `${t.serverHost}:${t.serverPort}`,
                    username: t.authUsername ?? '',
                    outboundCallerId: t.outboundCallerId ?? '',
                    // Credentials are deliberately absent: an export is a file that ends up in
                    // email and chat, and a trunk password there is a trunk password everywhere.
                    registration: t.registerEnabled ? (t.registration ?? 'Unknown') : 'n/a',
                  })),
                )
              }
            >
              <Download className="mr-1.5 size-4" />
              Export
            </Button>
          </span>
        }
        onEdit={(t) => setEditing(t.id)}
        onDelete={(rows) => remove.mutate(rows)}
        deleteLabel={(rows) =>
          rows.length === 1
            ? `Delete "${rows[0]!.name}"? Calls using it will fail immediately.`
            : `Delete ${rows.length} trunks? Calls using them will fail immediately.`
        }
        empty={
          isLoading ? null : (
            <div className="py-10 text-center">
              <p className="font-medium">No trunks yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add one to make and receive calls outside this PBX.
              </p>
            </div>
          )
        }
      />
    </div>
  );
}

/**
 * Live reachability, not just "configured".
 *
 * `contacts` comes from ps_contacts, which Asterisk writes itself, so a trunk showing a contact
 * is genuinely reachable. A peer trunk never registers, so absence of a contact there is normal
 * rather than a fault — reporting it as down would be a permanent false alarm.
 */
function TrunkStatus({ trunk }: { trunk: Trunk }) {
  /**
   * The round trip to the provider, alongside the state.
   *
   * "Registered" over a link that takes two seconds to answer is not the same health as one
   * answering in three milliseconds, and when somebody is chasing broken audio the latency is
   * usually the more informative half.
   */
  const latency =
    typeof trunk.latencyMs === 'number' ? `Response time: ${trunk.latencyMs.toFixed(3)} ms` : null;

  if (!trunk.isActive) {
    return (
      <Tooltip content="Disabled. This trunk carries no calls until it is enabled.">
        <span className="inline-flex text-muted-foreground">
          <Ban className="size-5" />
        </span>
      </Tooltip>
    );
  }

  // A peer trunk authenticates by IP and never registers, so there is no registration to report.
  // Calling that "not registered" would be reporting a fault that does not exist.
  if (!trunk.registerEnabled) {
    return (
      <Tooltip
        content={
          <>
            <p className="font-medium">Peer trunk.</p>
            <p className="text-muted-foreground">
              Authenticates by IP address, so nothing registers.
            </p>
            {latency ? <p className="mt-1">{latency}</p> : null}
          </>
        }
      >
        <span className="inline-flex">
          <CheckCircle2 className="size-5 text-muted-foreground" />
        </span>
      </Tooltip>
    );
  }

  const state = trunk.registration ?? 'Unknown';
  const up = state === 'Registered';
  const trying = state === 'Registering' || state === 'Unregistered';

  const explanation = up
    ? null
    : trying
      ? 'The provider has not accepted this trunk yet.'
      : state === 'Rejected'
        ? 'Check the username and password, and that the provider allows this server’s IP address.'
        : 'Asterisk has no state for this trunk yet.';

  return (
    <Tooltip
      content={
        <>
          <p className="font-medium">{state}.</p>
          {explanation ? <p className="text-muted-foreground">{explanation}</p> : null}
          {latency ? <p className="mt-1">{latency}</p> : null}
          {!up && trunk.nextAttempt ? (
            <p className="mt-1 text-muted-foreground">Next attempt in {trunk.nextAttempt}s.</p>
          ) : null}
        </>
      }
    >
      <span className="inline-flex">
        {up ? (
          <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-500" />
        ) : trying ? (
          <CircleDashed className="size-5 text-amber-500" />
        ) : (
          <XCircle className="size-5 text-destructive" />
        )}
      </span>
    </Tooltip>
  );
}

// -------------------------------------------------------------------------------------------
// Editor
// -------------------------------------------------------------------------------------------

interface Draft {
  name: string;
  mode: TrunkMode;
  isActive: boolean;
  transport: 'udp' | 'tcp' | 'tls';
  serverHost: string;
  serverPort: number;
  fromDomain: string;
  authUsername: string;
  authPassword: string;
  fromUser: string;
  outboundProxy: string;
  registerEnabled: boolean;
  registrationExpiry: number;
  matchIps: string;
  codecs: string[];
  dtmfMode: 'rfc4733' | 'inband' | 'info' | 'auto';
  mediaEncryption: 'no' | 'sdes' | 'dtls';
  qualifyEnabled: boolean;
  t38Support: boolean;
  inbandProgress: boolean;
  rtpKeepalive: number;
  allowTransfer: boolean;
  sendPai: boolean;
  sendRpid: boolean;
  trustIdInbound: boolean;
  failoverCodes: number[];
  callRestriction: 'outbound' | 'all';
  maxChannels: string;
  dids: string;
  outboundCallerId: string;
}

const BLANK: Draft = {
  name: '',
  mode: 'register',
  isActive: true,
  transport: 'udp',
  serverHost: '',
  serverPort: 5060,
  fromDomain: '',
  authUsername: '',
  authPassword: '',
  fromUser: '',
  outboundProxy: '',
  registerEnabled: true,
  registrationExpiry: 3600,
  matchIps: '',
  codecs: ['ulaw', 'alaw'],
  dtmfMode: 'rfc4733',
  mediaEncryption: 'no',
  qualifyEnabled: true,
  t38Support: false,
  inbandProgress: false,
  rtpKeepalive: 0,
  allowTransfer: true,
  sendPai: false,
  sendRpid: false,
  trustIdInbound: false,
  failoverCodes: [408, 500, 502, 503],
  callRestriction: 'all',
  maxChannels: '',
  dids: '',
  outboundCallerId: '',
};

function toDraft(t: TrunkDetail): Draft {
  return {
    name: t.name,
    mode: t.mode,
    isActive: t.isActive,
    transport: t.transport,
    serverHost: t.serverHost,
    serverPort: t.serverPort,
    fromDomain: t.fromDomain ?? '',
    authUsername: t.authUsername ?? '',
    // Never prefilled. The server does not serve it back, and an update that omits it keeps the
    // stored one — so an admin editing an unrelated field cannot blank a working credential.
    authPassword: '',
    fromUser: t.fromUser ?? '',
    outboundProxy: t.outboundProxy ?? '',
    registerEnabled: t.registerEnabled,
    registrationExpiry: t.registrationExpiry,
    matchIps: (t.matchIps ?? []).join(', '),
    codecs: t.codecs,
    dtmfMode: t.dtmfMode,
    mediaEncryption: t.mediaEncryption,
    qualifyEnabled: t.qualifyFrequency > 0,
    t38Support: t.t38Support,
    inbandProgress: t.inbandProgress,
    rtpKeepalive: t.rtpKeepalive,
    allowTransfer: t.allowTransfer,
    sendPai: t.sendPai,
    sendRpid: t.sendRpid,
    trustIdInbound: t.trustIdInbound,
    failoverCodes: t.failoverCodes,
    callRestriction: t.callRestriction,
    maxChannels: t.maxChannels === null ? '' : String(t.maxChannels),
    dids: t.dids.join('\n'),
    outboundCallerId: t.outboundCallerId ?? '',
  };
}

function toPayload(d: Draft) {
  const list = (raw: string) =>
    raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  return {
    name: d.name.trim(),
    mode: d.mode,
    isActive: d.isActive,
    transport: d.transport,
    // An account trunk has no provider address, but the column is not nullable and the
    // endpoint needs somewhere to point. Our own hostname is the honest placeholder.
    serverHost:
      d.mode === 'account' ? d.serverHost.trim() || window.location.hostname : d.serverHost.trim(),
    serverPort: d.serverPort,
    codecs: d.codecs,
    dtmfMode: d.dtmfMode,
    mediaEncryption: d.mediaEncryption,
    // The UI offers qualify as a switch because "how often, in seconds" is a question nobody
    // wants asked; 60s is what every provider is happy with and 0 is what "off" means to
    // Asterisk.
    qualifyFrequency: d.qualifyEnabled ? 60 : 0,
    t38Support: d.t38Support,
    inbandProgress: d.inbandProgress,
    rtpKeepalive: d.rtpKeepalive,
    allowTransfer: d.allowTransfer,
    sendPai: d.sendPai,
    sendRpid: d.sendRpid,
    trustIdInbound: d.trustIdInbound,
    failoverCodes: d.failoverCodes,
    callRestriction: d.callRestriction,
    dids: list(d.dids),
    matchIps: d.mode === 'ip_auth' ? list(d.matchIps) : [],
    registerEnabled: d.mode === 'register' ? d.registerEnabled : false,
    registrationExpiry: d.registrationExpiry,
    ...(d.maxChannels.trim() ? { maxChannels: Number(d.maxChannels) } : {}),
    ...(d.fromDomain.trim() ? { fromDomain: d.fromDomain.trim() } : {}),
    ...(d.fromUser.trim() ? { fromUser: d.fromUser.trim() } : {}),
    ...(d.outboundProxy.trim() ? { outboundProxy: d.outboundProxy.trim() } : {}),
    ...(d.outboundCallerId.trim() ? { outboundCallerId: d.outboundCallerId.trim() } : {}),
    ...(d.authUsername.trim() ? { authUsername: d.authUsername.trim() } : {}),
    ...(d.authPassword ? { authPassword: d.authPassword } : {}),
  };
}

function TrunkEditor({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [tab, setTab] = useState('basic');
  const [draft, setDraft] = useState<Draft>(BLANK);

  const { data: existing } = useQuery({
    queryKey: ['trunk', id],
    queryFn: () => api.get<TrunkDetail>(`/api/v1/trunks/${id}`),
    enabled: id !== null,
  });

  useEffect(() => {
    if (existing) setDraft(toDraft(existing));
  }, [existing]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = useMutation({
    mutationFn: () =>
      id === null
        ? api.post('/api/v1/trunks', toPayload(draft))
        : api.put(`/api/v1/trunks/${id}`, toPayload(draft)),
    onSuccess: onClose,
  });

  const credentialed = draft.mode === 'register' || draft.mode === 'account';
  const needsPassword = credentialed && id === null;

  const tabs = [
    { id: 'basic', label: 'Basic' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'dids', label: 'DIDs / DDIs' },
    { id: 'dod', label: 'Outbound caller ID' },
  ];

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title={id === null ? 'Add trunk' : draft.name || 'Edit trunk'}
        description="Settings for one connection to a telephone provider."
      />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'basic' && (
        <div className="space-y-5">
          <Section title="Basic">
            <Grid>
              <Row label="Name" required hint="Only used to identify this trunk in the admin.">
                <Input
                  value={draft.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Main carrier"
                  required
                />
              </Row>
              <Row
                label="Trunk status"
                hint="A disabled trunk carries no calls in either direction."
              >
                <div className="flex h-9 items-center gap-3">
                  <Toggle
                    checked={draft.isActive}
                    onChange={(v) => set('isActive', v)}
                    label="Trunk status"
                  />
                  <span className="text-sm text-muted-foreground">
                    {draft.isActive ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </Row>

              <Row
                label="Trunk type"
                span
                required
                hint={TRUNK_TYPES.find((t) => t.value === draft.mode)?.description}
              >
                <Select value={draft.mode} onChange={(v) => set('mode', v as TrunkMode)}>
                  {TRUNK_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Row>
            </Grid>
          </Section>

          <Section title="Connection">
            <Grid>
              <Row label="Transport" required>
                <Select
                  value={draft.transport}
                  onChange={(v) => set('transport', v as Draft['transport'])}
                >
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                  <option value="tls">TLS</option>
                </Select>
              </Row>

              {draft.mode !== 'account' && (
                <>
                  <Row label="Hostname / IP" required>
                    <Input
                      value={draft.serverHost}
                      onChange={(e) => set('serverHost', e.target.value)}
                      placeholder="sip.provider.com"
                      required
                    />
                  </Row>
                  <Row label="Port" required>
                    <Input
                      type="number"
                      value={draft.serverPort}
                      onChange={(e) => set('serverPort', Number(e.target.value))}
                      required
                    />
                  </Row>
                  <Row
                    label="Domain"
                    hint="Used in the From header. If the provider did not give you one, use the hostname."
                  >
                    <Input
                      value={draft.fromDomain}
                      onChange={(e) => set('fromDomain', e.target.value)}
                      placeholder="sip.provider.com"
                    />
                  </Row>
                </>
              )}

              {credentialed && (
                <>
                  <Row
                    label="Username"
                    required
                    hint={
                      draft.mode === 'account'
                        ? 'You choose this. The far end registers with it, and it acts as the trunk number.'
                        : undefined
                    }
                  >
                    <Input
                      value={draft.authUsername}
                      onChange={(e) => set('authUsername', e.target.value)}
                      autoComplete="off"
                      required
                    />
                  </Row>
                  <Row
                    label="Password"
                    required={needsPassword}
                    hint={
                      id === null
                        ? undefined
                        : existing?.hasAuthPassword
                          ? 'A password is stored. Leave blank to keep it.'
                          : 'No password is stored for this trunk.'
                    }
                  >
                    <Input
                      type="password"
                      value={draft.authPassword}
                      onChange={(e) => set('authPassword', e.target.value)}
                      autoComplete="new-password"
                      placeholder={id === null ? '' : '••••••••'}
                      required={needsPassword}
                    />
                  </Row>
                </>
              )}

              {draft.mode === 'ip_auth' && (
                <Row
                  label="Provider IP addresses"
                  span
                  required
                  // Every provider address must be listed or inbound calls from the missing one
                  // are rejected as unidentified — a failure that looks like the provider's fault.
                  hint="One per line or comma-separated. Include every address the provider sends from, or calls from the missing one are rejected."
                >
                  <textarea
                    className="min-h-20 w-full rounded-[--radius] border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={draft.matchIps}
                    onChange={(e) => set('matchIps', e.target.value)}
                    placeholder={'203.0.113.10\n198.51.100.0/24'}
                    required
                  />
                </Row>
              )}

              {draft.mode === 'register' && (
                <>
                  <Row label="Register to the provider">
                    <div className="flex h-9 items-center gap-3">
                      <Toggle
                        checked={draft.registerEnabled}
                        onChange={(v) => set('registerEnabled', v)}
                        label="Register to the provider"
                      />
                      <span className="text-sm text-muted-foreground">
                        {draft.registerEnabled ? 'On' : 'Off'}
                      </span>
                    </div>
                  </Row>
                  <Row label="Registration expiry" hint="Seconds. The provider may shorten it.">
                    <Input
                      type="number"
                      value={draft.registrationExpiry}
                      onChange={(e) => set('registrationExpiry', Number(e.target.value))}
                    />
                  </Row>
                </>
              )}

              {draft.mode !== 'account' && (
                <>
                  <Row
                    label="Outbound proxy"
                    hint="Only if the provider asked for one. All SIP is sent here instead."
                  >
                    <Input
                      value={draft.outboundProxy}
                      onChange={(e) => set('outboundProxy', e.target.value)}
                      placeholder="proxy.provider.com"
                    />
                  </Row>
                  <Row label="From user" hint="Overrides the user part of the From header.">
                    <Input
                      value={draft.fromUser}
                      onChange={(e) => set('fromUser', e.target.value)}
                    />
                  </Row>
                </>
              )}
            </Grid>
          </Section>
        </div>
      )}

      {tab === 'advanced' && <AdvancedTab draft={draft} set={set} />}

      {tab === 'dids' && (
        <Section title="DIDs / DDIs" description="The numbers this trunk is allowed to deliver.">
          <Row
            label="DID numbers"
            span
            // Enforced in the engine before route matching, not merely descriptive.
            hint="One per line. Leave empty to accept any number the provider sends. When the list is set, a call to a number that is not on it is refused before inbound routing runs."
          >
            <textarea
              className="min-h-40 w-full rounded-[--radius] border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={draft.dids}
              onChange={(e) => set('dids', e.target.value)}
              placeholder={'+442071234567\n+442071234568'}
            />
          </Row>
          <p className="mt-4 text-sm text-muted-foreground">
            Where each number goes is set on{' '}
            <span className="font-medium text-foreground">Call Control → Inbound Route</span>. This
            list only controls which numbers the trunk may deliver at all.
          </p>
        </Section>
      )}

      {tab === 'dod' && <DodTab trunkId={id} draft={draft} set={set} dods={existing?.dods ?? []} />}

      {save.error ? (
        <Alert tone="destructive">
          {save.error instanceof ApiError ? save.error.message : String(save.error)}
        </Alert>
      ) : null}

      <FormFooter onCancel={onClose} saving={save.isPending} saveLabel="Save trunk" />
    </form>
  );
}

function AdvancedTab({
  draft,
  set,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
}) {
  const codes = useMemo(() => supportedFailoverCodes(), []);
  const inert = useMemo(() => undetectableSipCodes(draft.failoverCodes), [draft.failoverCodes]);

  const toggleCodec = (codec: string) =>
    set(
      'codecs',
      draft.codecs.includes(codec)
        ? draft.codecs.filter((c) => c !== codec)
        : [...draft.codecs, codec],
    );

  return (
    <div className="space-y-5">
      <Section
        title="Codecs"
        description="Offered in this order. Match the list your provider supports."
      >
        <div className="flex flex-wrap gap-2">
          {CODECS.map((c) => {
            const on = draft.codecs.includes(c.value);
            const position = draft.codecs.indexOf(c.value) + 1;
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => toggleCodec(c.value)}
                className={cn(
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                  on
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/30',
                )}
              >
                {on && (
                  <span className="grid size-4 place-items-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                    {position}
                  </span>
                )}
                {c.label}
              </button>
            );
          })}
        </div>
        {draft.codecs.length === 0 && (
          <p className="mt-3 text-xs text-destructive">
            With no codecs selected the trunk falls back to the system defaults.
          </p>
        )}
      </Section>

      <Section title="VoIP settings">
        <Grid>
          <Row
            label="DTMF mode"
            hint="RFC4733 is right for almost every provider. Change it only if tones are not registering."
          >
            <Select
              value={draft.dtmfMode}
              onChange={(v) => set('dtmfMode', v as Draft['dtmfMode'])}
            >
              <option value="rfc4733">RFC4733 (RFC2833)</option>
              <option value="info">Info</option>
              <option value="inband">Inband</option>
              <option value="auto">Auto</option>
            </Select>
          </Row>

          <Row label="Media encryption (SRTP)">
            <Select
              value={draft.mediaEncryption}
              onChange={(v) => set('mediaEncryption', v as Draft['mediaEncryption'])}
            >
              <option value="no">None</option>
              <option value="sdes">SDES</option>
              <option value="dtls">DTLS</option>
            </Select>
          </Row>

          <SwitchRow
            label="Qualify"
            hint="Sends a SIP OPTIONS ping so the trunk's reachability is known before a call needs it."
            checked={draft.qualifyEnabled}
            onChange={(v) => set('qualifyEnabled', v)}
          />

          <SwitchRow
            label="T.38 fax"
            hint="Adds performance cost to every call on this trunk. Leave off unless you send faxes over it."
            checked={draft.t38Support}
            onChange={(v) => set('t38Support', v)}
          />

          <SwitchRow
            label="Inband progress"
            hint="Send 183 Session Progress and start passing audio, instead of 180 Ringing. Turn on if callers hear silence instead of ringing."
            checked={draft.inbandProgress}
            onChange={(v) => set('inbandProgress', v)}
          />

          <SwitchRow
            label="Accept SIP REFER"
            hint="Honour transfers the provider asks for. Off answers them with a rejection."
            checked={draft.allowTransfer}
            onChange={(v) => set('allowTransfer', v)}
          />

          <Row
            label="RTP keep-alive"
            hint="Seconds between comfort-noise frames on a silent call, to hold NAT and firewall pinholes open. 0 disables. Try 1–5 if audio dies after a period of hold."
          >
            <Input
              type="number"
              min={0}
              max={30}
              value={draft.rtpKeepalive}
              onChange={(e) => set('rtpKeepalive', Number(e.target.value))}
            />
          </Row>

          <SwitchRow
            label="Send P-Asserted-Identity"
            hint="Most carriers use PAI to carry the real caller ID on outbound calls."
            checked={draft.sendPai}
            onChange={(v) => set('sendPai', v)}
          />

          <SwitchRow
            label="Send Remote-Party-ID"
            hint="The older equivalent of PAI. Some carriers still want it."
            checked={draft.sendRpid}
            onChange={(v) => set('sendRpid', v)}
          />

          <SwitchRow
            label="Trust inbound caller ID"
            hint="Take PAI and RPID from the provider at face value. Only turn on for a carrier you trust — anyone who can reach this trunk can otherwise claim any caller ID."
            checked={draft.trustIdInbound}
            onChange={(v) => set('trustIdInbound', v)}
          />
        </Grid>
      </Section>

      <Section
        title="Outbound failover"
        description="Which provider rejections should move the call on to the next trunk in the route."
      >
        <div className="flex flex-wrap gap-2">
          {codes.map((code) => {
            const on = draft.failoverCodes.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() =>
                  set(
                    'failoverCodes',
                    on
                      ? draft.failoverCodes.filter((c) => c !== code)
                      : [...draft.failoverCodes, code].sort((a, b) => a - b),
                  )
                }
                className={cn(
                  'rounded-full border px-3 py-1.5 font-mono text-xs transition-colors',
                  on
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/30',
                )}
              >
                {code}
              </button>
            );
          })}
        </div>

        {/*
          Said out loud rather than implied. Asterisk translates a SIP response into a Q.931
          cause before we ever see the leg, and that translation is many-to-one — so the honest
          thing is to tell an admin that 500 and 503 cannot be told apart, not to render two
          switches that pretend they can.
        */}
        <p className="mt-4 text-xs text-muted-foreground">
          Some of these cannot be told apart once the call has failed:{' '}
          <span className="font-mono">500</span> and <span className="font-mono">503</span> arrive
          identically, as do <span className="font-mono">408</span> and{' '}
          <span className="font-mono">504</span>. Selecting either one of a pair fails over on both.
          Busy and declined are never offered — the far end answered for itself, and retrying would
          ring a real person twice.
        </p>

        {inert.length > 0 && (
          <Alert tone="warning" className="mt-3">
            {inert.join(', ')} {inert.length === 1 ? 'is' : 'are'} stored but has no effect on this
            platform — Asterisk does not report {inert.length === 1 ? 'it' : 'them'} distinctly.
          </Alert>
        )}
      </Section>

      <Section
        title="Call restriction"
        description="Cap how many calls this trunk carries at once."
      >
        <Grid>
          <Row label="Applies to">
            <Select
              value={draft.callRestriction}
              onChange={(v) => set('callRestriction', v as Draft['callRestriction'])}
            >
              <option value="all">All calls</option>
              <option value="outbound">Outbound calls only</option>
            </Select>
          </Row>
          <Row
            label="Maximum concurrent calls"
            hint="Leave empty for unlimited. Outbound calls beyond the limit take the next trunk in the route, or hear congestion if there is none."
          >
            <Input
              type="number"
              min={1}
              value={draft.maxChannels}
              onChange={(e) => set('maxChannels', e.target.value)}
              placeholder="Unlimited"
            />
          </Row>
        </Grid>
        <p className="mt-4 text-xs text-muted-foreground">Emergency calls ignore this limit.</p>
      </Section>
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Row label={label} hint={hint}>
      <div className="flex h-9 items-center gap-3">
        <Toggle checked={checked} onChange={onChange} label={label} />
        <span className="text-sm text-muted-foreground">{checked ? 'On' : 'Off'}</span>
      </div>
    </Row>
  );
}

/**
 * DOD — which number an extension presents when it calls out over this trunk.
 *
 * Per trunk rather than global because a carrier rejects any caller ID that is not a number you
 * own with THAT carrier, so the same extension has to present differently on each one.
 */
function DodTab({
  trunkId,
  draft,
  set,
  dods,
}: {
  trunkId: string | null;
  draft: Draft;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  dods: TrunkDod[];
}) {
  const queryClient = useQueryClient();
  const [number, setNumber] = useState('');
  const [extensionId, setExtensionId] = useState('');

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/trunks/${trunkId}/dods`, {
        dodNumber: number.trim(),
        extensionId: extensionId || null,
      }),
    onSuccess: () => {
      setNumber('');
      setExtensionId('');
      void queryClient.invalidateQueries({ queryKey: ['trunk', trunkId] });
    },
  });

  const remove = useMutation({
    mutationFn: (dodId: string) => api.del(`/api/v1/trunks/${trunkId}/dods/${dodId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['trunk', trunkId] }),
  });

  const nameFor = (id: string | null) => {
    if (!id) return 'Any extension';
    const ext = extensions.find((e) => e.id === id);
    return ext ? `${ext.number} — ${ext.name}` : 'Unknown extension';
  };

  return (
    <div className="space-y-5">
      <Section
        title="Default outbound caller ID"
        description="Presented on calls over this trunk when nothing more specific applies."
      >
        <Grid>
          <Row
            label="Caller ID"
            hint="Must be a number the provider has authorised you to present, or they will reject the call."
          >
            <Input
              value={draft.outboundCallerId}
              onChange={(e) => set('outboundCallerId', e.target.value)}
              placeholder="+442071234567"
            />
          </Row>
        </Grid>
      </Section>

      <Section
        title="Per-extension caller ID (DOD)"
        description="Overrides the default for one extension on this trunk."
      >
        {trunkId === null ? (
          <p className="text-sm text-muted-foreground">
            Save the trunk first — per-extension numbers attach to a trunk that exists.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-48 flex-1">
                <Row label="Number">
                  <Input
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="+442071234567"
                  />
                </Row>
              </div>
              <div className="min-w-56 flex-1">
                <Row label="Extension">
                  <Select value={extensionId} onChange={setExtensionId}>
                    <option value="">Any extension</option>
                    {extensions.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.number} — {e.name}
                      </option>
                    ))}
                  </Select>
                </Row>
              </div>
              {/*
                type="button": this control lives inside the trunk form, and a bare <button>
                inside a <form> submits it. That exact bug shipped once already on the IVR editor.
              */}
              <Button
                type="button"
                variant="secondary"
                disabled={!number.trim() || add.isPending}
                onClick={() => add.mutate()}
              >
                <Plus className="mr-1.5 size-4" />
                Add
              </Button>
            </div>

            {add.error ? (
              <Alert tone="destructive" className="mt-3">
                {add.error instanceof ApiError ? add.error.message : String(add.error)}
              </Alert>
            ) : null}

            {dods.length > 0 && (
              <ul className="mt-5 divide-y divide-border rounded-[--radius] border border-border">
                {dods.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="font-mono text-sm">{d.dodNumber}</span>
                    <Badge>{nameFor(d.extensionId)}</Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      className="ml-auto text-destructive"
                      onClick={() => remove.mutate(d.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
